import React from 'react';
import { ProgressBar, Badge } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { getNativeLanguageName } from '../constants/languageNames';

/**
 * Maps workflow technical step → { stageIndex (0-4), label, isWaiting }
 * Stages: 0=Story, 1=Pages, 2=Assembly, 3=Printing, 4=Shipping
 */
const STEP_TO_STAGE = {
    // Stage 0 – Writing story
    A_start:             { stage: 0, label: 'progressBar.writingStory',       waiting: false },
    B_generate_story:    { stage: 0, label: 'progressBar.writingStory',       waiting: false },
    B1_generate_story:   { stage: 0, label: 'progressBar.writingStory',       waiting: false },
    B2_translate_story:  { stage: 0, label: 'progressBar.translatingStory',   waiting: false },
    C_qa_story:          { stage: 0, label: 'progressBar.reviewingStory',     waiting: false },
    D_approve_story:     { stage: 0, label: 'progressBar.storyReady',         waiting: true },

    // Stage 1 – Pages (includes cover generation)
    E_generate_cover:    { stage: 1, label: 'progressBar.creatingCover',      waiting: false },
    E1_generate_cover:   { stage: 1, label: 'progressBar.creatingCover',      waiting: false },
    E2_translate_cover:  { stage: 1, label: 'progressBar.translatingCover',   waiting: false },
    F_approve_cover:     { stage: 1, label: 'progressBar.illustratingPages',  waiting: false },
    G_generate_pages:    { stage: 1, label: 'progressBar.illustratingPages',  waiting: false },
    G1_generate_pages:   { stage: 1, label: 'progressBar.illustratingPages',  waiting: false },
    G2_translate_back:   { stage: 1, label: 'progressBar.finishingPages',     waiting: false },
    H_approve_pages:     { stage: 1, label: 'progressBar.pagesReady',         waiting: true },

    // Stage 2 – Assembly
    I_assemble:          { stage: 2, label: 'progressBar.assemblingPdf',      waiting: false },
    J_approve_final:     { stage: 2, label: 'progressBar.pdfReady',           waiting: true },

    // Stage 3/4 are resolved from order fulfillment once generation is completed
    completed:           { stage: 3, label: 'progressBar.printingInProgress', waiting: false },
};

const STAGES = [
    { icon: '✍️', name: 'progressBar.story' },
    { icon: '🖼️', name: 'progressBar.pages' },
    { icon: '📚', name: 'progressBar.assembly' },
    { icon: '🖨️', name: 'progressBar.printing' },
    { icon: '🚚', name: 'progressBar.shipping' },
];

function getStageInfo(job, order = null) {
    if (!job) return null;

    if (job.status === 'failed') {
        return { stage: -1, label: 'progressBar.failed', errorMessage: job.error_message, waiting: false, failed: true };
    }
    if (job.status === 'completed') {
        const gelato = order?.customization?.gelato || {};
        const fulfillmentStatus = (gelato.fulfillmentStatus || '').toString().toLowerCase();
        const hasTracking = Boolean(
            gelato.trackingCode ||
            gelato.trackingUrl ||
            (Array.isArray(gelato.tracking) && gelato.tracking.length > 0)
        );
        const shippingStarted =
            hasTracking ||
            /ship|transit|dispatch|delivery|delivered/.test(fulfillmentStatus);

        if (order?.is_delivered) {
            return { stage: 4, label: 'progressBar.shipped', waiting: false, failed: false };
        }

        if (shippingStarted) {
            return { stage: 4, label: 'progressBar.shippingInProgress', waiting: false, failed: false };
        }

        return { stage: 3, label: 'progressBar.printingInProgress', waiting: false, failed: false };
    }

    const step = job.current_step || 'A_start';
    const info = STEP_TO_STAGE[step];
    if (info) return { ...info, failed: false };

    // Fallback: map status
    const statusMap = {
        pending:           { stage: 0, label: 'progressBar.starting',          waiting: false },
        story_generated:   { stage: 0, label: 'progressBar.storyReadyBadge',   waiting: true  },
        story_qa:          { stage: 0, label: 'progressBar.reviewingStory',     waiting: false },
        awaiting_approval: { stage: 0, label: 'progressBar.awaitingApproval',   waiting: true },
        images_generating: { stage: 1, label: 'progressBar.creatingArtwork',    waiting: false },
        images_qa:         { stage: 1, label: 'progressBar.checkingImages',     waiting: false },
        assembling:        { stage: 2, label: 'progressBar.assemblingPdf',      waiting: false },
    };
    return { ...(statusMap[job.status] || { stage: 0, label: 'progressBar.inProgressBadge', waiting: false }), failed: false };
}

/**
 * Compact badge-style indicator for tables/list views.
 * Shows a coloured badge with status text.
 */
export function BookStatusBadge({ job, order, status, currentStep }) {
    const { t } = useTranslation();
    const resolvedJob = job || (status ? { status, current_step: currentStep } : null);
    if (!resolvedJob) return <Badge bg="secondary">{t('progressBar.notStarted')}</Badge>;

    const info = getStageInfo(resolvedJob, order);
    if (info.failed)   return <Badge bg="danger">{t('progressBar.failedBadge')}</Badge>;
    if (info.stage >= 4) return <Badge bg="success">{t('progressBar.shipping')}</Badge>;
    if (info.stage === 3) return <Badge bg="secondary">{t('progressBar.printing')}</Badge>;
    if (info.waiting)  return <Badge bg="warning" text="dark">{t('progressBar.needsApproval')}</Badge>;
    return <Badge bg="info">{t('progressBar.inProgress')}</Badge>;
}

/**
 * Full horizontal progress bar for detail views.
 * Shows all 5 stages with current position highlighted.
 */
export function BookProgressBar({ job, order = null, compact = false }) {
    const { t } = useTranslation();
    if (!job) {
        return (
            <div className="text-muted small fst-italic">{t('progressBar.noGeneration')}</div>
        );
    }

    const info = getStageInfo(job, order);

    if (info.failed) {
        return (
            <div>
                <ProgressBar now={100} variant="danger" label={t('progressBar.failedBadge')} />
                <small className="text-danger mt-1 d-block">{info.errorMessage || t(info.label)}</small>
            </div>
        );
    }

    const progressPct = info.stage >= 4 ? 100 : Math.round((info.stage / 4) * 100);
    const language = (job.input_data || {}).language || job.language;
    const languageLabel = getNativeLanguageName(language, language);

    return (
        <div>
            {/* Stage step indicators */}
            <div className="d-flex justify-content-between mb-1" style={{ fontSize: compact ? '0.7rem' : '0.75rem' }}>
                {STAGES.map((s, i) => {
                    const done    = i < info.stage;
                    const current = i === info.stage;
                    return (
                        <div
                            key={i}
                            className={`text-center flex-fill ${done ? 'text-success' : current ? 'text-primary fw-bold' : 'text-muted'}`}
                            style={{ minWidth: 0 }}
                        >
                            <div style={{ fontSize: compact ? '0.9rem' : '1.1rem' }}>{done ? '✓' : s.icon}</div>
                            {!compact && <div>{t(s.name)}</div>}
                        </div>
                    );
                })}
            </div>

            {/* Progress bar */}
            <ProgressBar
                now={progressPct}
                variant={info.stage >= 4 ? 'success' : info.waiting ? 'warning' : 'info'}
                animated={!info.waiting && info.stage < 4}
                style={{ height: compact ? '6px' : '10px' }}
            />

            {/* Status label */}
            <div className="d-flex justify-content-between align-items-center mt-1">
                <small className={info.waiting ? 'text-warning fw-semibold' : 'text-muted'}>
                    {info.waiting && '⚠ '}{t(info.label)}
                </small>
                {language && String(language).toLowerCase() !== 'english' && (
                    <small className="text-muted ms-2">🌐 {languageLabel}</small>
                )}
            </div>
        </div>
    );
}

export default BookProgressBar;
